import { Box, Button, Flex, InlineNotification, Modal, Text, useToast } from "@familyzone/component-library";
import React, { ChangeEvent, useState } from "react";
import "../../../../../../css/Classrooms.css";
import ImportClassroomFileList from "../ImportClassroomFileList";
import { batchCreateClassrooms } from "../../../../../utils/api/Classrooms";
import { Accordion, AccordionItem, AccordionPanel, AccordionButton } from "@chakra-ui/react";
import { NewScheduleImports } from ".";
import { ScheduleCSVObject } from "./ScheduleCSVObject";
import { importClassroomSchedules, isImportSchedulesValidationError, ScheduleRule } from "../../../../../utils/api/classroomSchedules";
import { datadogRum } from "@datadog/browser-rum";
import { getValidCSVFiles, UniqueClassroomEntries, getUniqueClassroomEntryArray } from "./utils";
import { ClassroomCSVObject } from "../ClassroomCSVObject";

interface Props {
  open: boolean;
  onClose: () => void;
}

export const ImportCSVModal = (props: Props): JSX.Element => {
  const { successToast, errorToast } = useToast();

  const [uniqueClassroomEntries, setUniqueClassroomEntries] = useState<UniqueClassroomEntries>({});
  const [classroomFiles, setClassroomFiles] = useState(new Set<File>());
  const [scheduleObjects, setScheduleObjects] = useState(new Set<ScheduleCSVObject>());
  const [importing, setImporting] = useState(false);

  const handleHide = () => {
    setUniqueClassroomEntries({});
    setClassroomFiles(new Set());
    setScheduleObjects(new Set());
    setImporting(false);
    props.onClose();
  };

  const handleCreateClassrooms = () => {
    const classrooms = JSON.stringify(uniqueClassroomEntries);
    batchCreateClassrooms(classrooms)
      .then(() => {
        handleHide();
        successToast({ title: "Success!", description: "We've received the request and it has been sent for processing." });
      })
      .catch(() => {
        errorToast({
          title: "Error",
          description:
            "We're not able to process your file(s) at this time. Please ensure your file(s) are formatted correctly. See the example format on the form.",
        });
      })
      .finally(() => {
        setImporting(false);
      });
  };

  function handleImportSchedules(schedulesToImport: ScheduleRule[]): void {
    importClassroomSchedules(schedulesToImport)
      .then((response) => {
        // Scenario 1: Import completed successfully (we no longer support partial imports if some fail)
        successToast({
          title: "Import complete",
          isClosable: true,
          description: `Successfully imported ${response.schedulesImported} schedules`,
        });
        handleHide();
      })
      .catch((error) => {
        if (isImportSchedulesValidationError(error)) {
          // Scenario 2: Entire import failed due to ANY backend validation errors
          const validationErrors = error.validationErrors;
          errorToast({
            title: "Import failed. Please address the errors and try again.",
            isClosable: true,
            children: (
              <Accordion allowToggle>
                <AccordionItem>
                  {validationErrors.slice(0, 2).map((error, index) => (
                    <Text key={index} fontStyle="italic" my={10}>
                      {error.className} - {error.username} - {error.error}
                    </Text>
                  ))}
                  {validationErrors.length > 2 && (
                    <>
                      <AccordionPanel>
                        {validationErrors.slice(2).map((error, index) => (
                          <Text key={index + 2} fontStyle="italic">
                            {error.className} - {error.username} - {error.error}
                          </Text>
                        ))}
                      </AccordionPanel>
                      <AccordionButton justifyContent="end">Show all errors</AccordionButton>
                    </>
                  )}
                </AccordionItem>
              </Accordion>
            ),
          });
        } else {
          // Scenario 3: Import failed due to unexpected error
          console.error("unexpected error importing schedules", error);
          datadogRum.addError(error);
          errorToast({
            title: "Error",
            isClosable: true,
            description:
              "We're not able to process your file(s) at this time. Please ensure your file(s) are formatted correctly. See the example format on the form.",
          });
        }
      })
      .finally(() => {
        setImporting(false);
      });
  }

  const handleParseError = (csvObject: ScheduleCSVObject) => {
    // Remove the invalid CSV object from the set
    const updatedObjects = new Set(Array.from(scheduleObjects).filter((obj) => obj !== csvObject));
    setScheduleObjects(updatedObjects);

    // Show error toast
    errorToast({
      title: "Invalid CSV file",
      description: csvObject.getErrorMessage(),
      isClosable: true,
    });
  };

  const handleSubmit = () => {
    setImporting(true);
    if (Object.keys(uniqueClassroomEntries).length > 0) {
      handleCreateClassrooms();
    } else if (scheduleObjects.size > 0) {
      // Filter out any objects with errors before processing
      const validObjects = Array.from(scheduleObjects).filter((obj) => !obj.getHasError());

      if (validObjects.length === 0) {
        errorToast({
          title: "No valid files",
          description: "All uploaded files contain errors. Please check the CSV format and try again.",
          isClosable: true,
        });
        setImporting(false);
        return;
      }

      const scheduleRules: ScheduleRule[] = [];
      for (const csvObject of validObjects) {
        scheduleRules.push(...csvObject.getScheduleRules());
      }

      handleImportSchedules(scheduleRules);
    } else {
      setTimeout(() => {
        setImporting(false);
        handleHide();
      }, 500);
    }
  };

  const handleUpdateClassroomCsvObject = (classroomCsvObjects: Set<ClassroomCSVObject>) => {
    setUniqueClassroomEntries(getUniqueClassroomEntryArray(classroomCsvObjects));
  };

  const handleDeleteClassroomCsvObject = (csvObject: ClassroomCSVObject) => {
    const updatedFiles = new Set(classroomFiles);
    updatedFiles.delete(csvObject.getFile() as File);
    setClassroomFiles(updatedFiles);
  };

  const handleClassroomFileSelect = (evt: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(evt.target.files ?? []);
    setClassroomFiles(addFiles(files, classroomFiles));
  };

  function addFiles(files: File[], currentFileList: Set<File>) {
    const validFiles = getValidCSVFiles(files);
    const updatedSet = new Set(currentFileList);
    for (const file of validFiles) {
      if (
        Array.from(updatedSet).filter(
          (innerFile) => innerFile.name === file.name && innerFile.size === file.size && innerFile.lastModified === file.lastModified
        ).length === 0
      ) {
        updatedSet.add(file);
      }
    }
    return updatedSet;
  }

  if (props.open) {
    return (
      <Modal headerText={"Import CSV"} onClose={handleHide} isOpen={props.open} size="md" contentProps={{ style: { overflow: "auto" } }}>
        <form className="mui-form">
          <div style={{ display: scheduleObjects.size === 0 ? "block" : "none" }}>
            <div className="mui-textfield" id={"file_picker_1"}>
              <input
                type="file"
                id="files_1"
                data-testid="files_1"
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                onChange={handleClassroomFileSelect}
                multiple
              />
              <Button variant="primary" data-testid="upload-classrooms-button">
                <label className="importLabel" htmlFor="files_1">
                  Upload Classrooms
                </label>
              </Button>
              <InlineNotification
                status="info"
                notificationTitle={"Expected Classrooms CSV Format"}
                notificationDescription={
                  <Box>
                    <div>Columns: user, class, is_owner</div>
                    <div>Example:</div>
                    <div>oscar.maccay, classroom1, true</div>
                    <div>steve.johnson, classroom1, false</div>
                  </Box>
                }
              />
              <ImportClassroomFileList
                files={classroomFiles}
                updateCsvObjectCallback={handleUpdateClassroomCsvObject}
                deleteCsvObjectCallback={handleDeleteClassroomCsvObject}
              />
            </div>
          </div>
          <div style={{ display: classroomFiles.size === 0 ? "block" : "none" }}>
            <NewScheduleImports
              scheduleObjects={scheduleObjects}
              updateCsvObjectsCallback={(csvObjects) => {
                setScheduleObjects(csvObjects);
              }}
              onParseError={handleParseError}
            />
          </div>
        </form>
        <Box mt={10}>
          {(classroomFiles.size > 0 || scheduleObjects.size > 0) && (
            <Flex width="100%">
              <Button onClick={handleSubmit} variant="primary" disabled={importing} data-testid="import-button">
                {importing ? "Importing" : "Import"}
              </Button>
            </Flex>
          )}
        </Box>
      </Modal>
    );
  }
  return <div />;
};
